/**
 * Gateways as the Discover step offers them: one card per gateway with its
 * status, product and firmware, and which one to pre-select (the only online
 * multi-protocol gateway, when there is exactly one). Pure, so it is tested
 * without a query client; the Connect route feeds it the things list, each
 * gateway's GET (its birth) and the product catalogue.
 */

import type { Thing } from "@/lib/api/types";
import { deriveStatus, productDrivers, type ThingProduct } from "@/lib/api/things";
import { isModbusDriver } from "./drivers";
import { hasBirth, readBirth, speaksConfigV2 } from "./gateway-birth";

export interface GatewayCard {
  id: string;
  name: string;
  /** online / warning / offline / unknown, as the gateways page shows it. */
  status: ReturnType<typeof deriveStatus>;
  /** Firmware from the birth ("0.4.1"), null before the GET answers or on older firmware. */
  fw: string | null;
  /** The product's label ("GatewayPro"), null when unknown. */
  productLabel: string | null;
  /** Runs protocols other than Modbus: its product allows them, or its birth accepts config v2. */
  multiProtocol: boolean;
  /** A factory / demo Thing. */
  simulated: boolean;
  /** The gateway announced a birth (GatewayPro v0.3+). */
  announced: boolean;
}

/** A gateway Thing (list row, plus its GET when loaded) as a card. */
export function gatewayCard(
  row: Thing,
  detail: Thing | null | undefined,
  products: ThingProduct[] | null | undefined,
): GatewayCard {
  const t = detail ?? row;
  const birth = readBirth(detail ?? null);
  const productId = t.product_id ?? row.product_id ?? null;
  const product = (products ?? []).find((p) => p.id === productId) ?? null;
  const drivers = productDrivers(product);
  return {
    id: row.id,
    name: t.name || row.name,
    status: deriveStatus(t.last_seen ?? row.last_seen, t.online ?? row.online),
    fw: birth.fw,
    productLabel: product?.label ?? null,
    multiProtocol: drivers.some((d) => !isModbusDriver(d)) || speaksConfigV2(birth),
    simulated: row.is_simulated === true,
    announced: hasBirth(detail ?? null),
  };
}

/** Cards in the order to offer them: online first, then multi-protocol, then real before demo, then by name. */
export function sortGatewayCards(cards: GatewayCard[]): GatewayCard[] {
  const rank = (c: GatewayCard) =>
    (c.status === "online" ? 0 : c.status === "warning" ? 1 : 2) * 4 +
    (c.multiProtocol ? 0 : 2) +
    (c.simulated ? 1 : 0);
  return [...cards].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

export interface GatewayPick {
  id: string | null;
  /**
   * True when the pick is the obvious one (the only gateway, or the only online
   * multi-protocol one), so the step can show it collapsed with a "Change".
   */
  obvious: boolean;
}

/**
 * Which gateway to pre-select: the only online multi-protocol gateway; else
 * the first online multi-protocol one; else the first online one; else the
 * first. Obvious when there is one gateway, or exactly one online multi-protocol one.
 */
export function preferredGateway(cards: GatewayCard[]): GatewayPick {
  if (cards.length === 0) return { id: null, obvious: false };
  if (cards.length === 1) return { id: cards[0].id, obvious: true };
  const sorted = sortGatewayCards(cards);
  const onlineMulti = sorted.filter((c) => c.status === "online" && c.multiProtocol);
  if (onlineMulti.length === 1) return { id: onlineMulti[0].id, obvious: true };
  if (onlineMulti.length > 1) return { id: onlineMulti[0].id, obvious: false };
  const online = sorted.find((c) => c.status === "online");
  return { id: (online ?? sorted[0]).id, obvious: false };
}

/** "Online · GatewayPro · fw 0.4.1" — the line under a gateway's name. */
export function gatewayCardLine(c: GatewayCard): string {
  const status =
    c.status === "online"
      ? "Online"
      : c.status === "offline"
        ? "Offline"
        : c.status === "warning"
          ? "Quiet"
          : "Status unknown";
  return [status, c.productLabel, c.fw ? `fw ${c.fw}` : null, c.simulated ? "demo" : null].filter(Boolean).join(" · ");
}
